import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase.js";
import { Badge, Modal } from "../lib/ui.js";
import { StatTile } from "../lib/StatTile.js";
import { formatDateTime } from "../lib/datetime.js";
import { formatAuPhoneForDisplay } from "../lib/phone.js";
import { CallDetailModal } from "./CallDetail.js";
import { LOOK_BACK_DAYS, questionableCalls, type CallToLookAt, type QuestionableItem } from "../lib/questionableCalls.js";

export interface CallsToReviewResult {
  items: QuestionableItem[];
  total: number;
}

/**
 * The calls worth a second look in the last LOOK_BACK_DAYS days (see lib/questionableCalls.ts): calls with an open review flag, and
 * enquiries that ended with no booking, no change and no callback. null until the first read. `reload` reads again (after one is marked
 * reviewed).
 */
export function useCallsToReview(businessId: string | null, refreshTick: number): { result: CallsToReviewResult | null; reload: () => void } {
  const [result, setResult] = useState<CallsToReviewResult | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    void (async () => {
      const since = new Date(Date.now() - LOOK_BACK_DAYS * 86_400_000).toISOString();
      const { data } = await supabase
        .from("v_call_list")
        .select("id, caller_number, started_at, duration_seconds, outcome, needs_review, reviewed_at")
        .eq("business_id", businessId)
        .gte("started_at", since)
        .order("started_at", { ascending: false })
        .limit(200);
      const calls = (data ?? []) as CallToLookAt[];
      const candidates = calls.filter((c) => c.needs_review || ((c.outcome ?? "").toLowerCase() === "enquiry" && !c.reviewed_at)).map((c) => c.id);

      const headlines = new Map<string, string>();
      const flagMessages = new Map<string, string>();
      const callbackCallIds = new Set<string>();
      if (candidates.length > 0) {
        const [sums, flags, callbacks] = await Promise.all([
          supabase.from("call_summaries").select("call_id, headline").in("call_id", candidates),
          supabase.from("review_flags").select("call_id, message, created_at").in("call_id", candidates).eq("status", "open").order("created_at"),
          supabase.from("callback_requests").select("call_id").in("call_id", candidates),
        ]);
        for (const s of (sums.data ?? []) as Array<{ call_id: string; headline: string | null }>) if (s.headline) headlines.set(s.call_id, s.headline);
        for (const f of (flags.data ?? []) as Array<{ call_id: string | null; message: string }>) if (f.call_id && !flagMessages.has(f.call_id)) flagMessages.set(f.call_id, f.message);
        for (const b of (callbacks.data ?? []) as Array<{ call_id: string | null }>) if (b.call_id) callbackCallIds.add(b.call_id);
      }
      if (!cancelled) setResult(questionableCalls(calls, headlines, flagMessages, callbackCallIds));
    })();
    return () => {
      cancelled = true;
    };
  }, [businessId, refreshTick, reloadTick]);

  return { result, reload: () => setReloadTick((t) => t + 1) };
}

/**
 * The Dashboard's "Calls to review" figure: the number of calls that need a look. Clicking it opens the full list in a pop-up; clicking a
 * call there opens it (where it can be marked reviewed). Not shown when there is nothing to review.
 */
export function CallsToReviewTile({ businessId, refreshTick }: { businessId: string; refreshTick: number }) {
  const { result, reload } = useCallsToReview(businessId, refreshTick);
  const [open, setOpen] = useState(false);
  const [openCallId, setOpenCallId] = useState<string | null>(null);

  // Once the last call has been reviewed there is nothing left to list: close the pop-up.
  useEffect(() => {
    if (result && result.total === 0) setOpen(false);
  }, [result]);

  if (!result || result.total === 0) return null;

  return (
    <>
      <StatTile label="Calls to review" value={result.total} onClick={() => setOpen(true)} attention />
      {open && (
        <Modal title="Calls to review" onClose={() => setOpen(false)} width={620}>
          <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--rc-text-muted)" }}>
            From the last {LOOK_BACK_DAYS} days. Open a call and mark it reviewed once you have looked at it.
          </p>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
            {result.items.map((it) => (
              <li key={it.id}>
                <button
                  onClick={() => setOpenCallId(it.id)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    cursor: "pointer",
                    background: "var(--rc-surface-sunken)",
                    border: "none",
                    borderLeft: `3px solid ${it.kind === "review" ? "var(--rc-warning)" : "var(--rc-border)"}`,
                    borderRadius: 8,
                    padding: "9px 12px",
                    color: "var(--rc-text)",
                    font: "inherit",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{it.reason}</span>
                    <Badge tone={it.kind === "review" ? "warning" : "neutral"}>{it.kind === "review" ? "Needs review" : "No booking made"}</Badge>
                  </div>
                  <div style={{ fontSize: 12.5, color: "var(--rc-text-muted)", marginTop: 2 }}>
                    {it.startedAt ? formatDateTime(it.startedAt) : "-"} · {it.caller ? formatAuPhoneForDisplay(it.caller) : "Unknown caller"}
                  </div>
                </button>
              </li>
            ))}
          </ul>
          {result.total > result.items.length && (
            <p style={{ margin: "10px 0 0", fontSize: 13, color: "var(--rc-text-muted)" }}>
              Showing the newest {result.items.length} of {result.total}. Review these and the rest will follow.
            </p>
          )}
        </Modal>
      )}
      {openCallId && <CallDetailModal callId={openCallId} onClose={() => setOpenCallId(null)} onChanged={reload} />}
    </>
  );
}
